import { AppDataHttp } from '../http.ts'
import { Customer } from '../types/user.types'

const headers = {
  'Content-type': 'application/json'
}

export const getCurrentBankingPartners = (accessToken?: string, customerCompanyId?: string) => {
  const path = customerCompanyId
    ? `/app-queries/current-banking-partners/${customerCompanyId}`
    : '/app-queries/current-banking-partners'

  return AppDataHttp.get(path, {
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    }
  })
}

export const getCurrentSystemUsersUsernames = (accessToken: string) => {
  return AppDataHttp.get('/app-queries/current-system-users-usernames', {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCurrentCustomerCompaniesBriefData = (selectedCountry: string, accessToken?: string) => {
  return AppDataHttp.get(`/app-queries/current-customer-companies-brief-data/${selectedCountry}`, {
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    }
  })
}

export const getCountriesAllowedToOperateData = (systemUserId: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/countries-allowed-to-operate-data/${systemUserId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getFundingsDataByCountry = (selectedCountry: string, accessToken?: string) => {
  return AppDataHttp.get(`/app-queries/fundings-data-by-country/${selectedCountry}`, {
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    }
  })
}

export const getFundingsDataByCustomerCompany = (customerCompanyId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/fundings-data-by-customer-company/${customerCompanyId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerData = (customerId: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customer-data/${customerId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getUnidentifiedCustomerData = (customerId: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/unidentified-customer-data/${customerId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomersDataWithSameDocumentNumber = (documentNumber: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customers-data-with-same-document-number/${documentNumber}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getPaymentsWithDuplicateReference = (paymentId: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/payments-with-duplicate-reference/${paymentId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomersBriefData = (systemUserId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customers-brief-data/${systemUserId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getProcessingBatchesData = (systemUserId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/processing-batches-data/${systemUserId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getBankBalancesBriefData = (systemUserId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/bank-balances-brief-data/${systemUserId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getInsightsBriefData = (payload: object, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/insights-brief-data',
    { ...payload },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const getBankingPartnersBalanceByCountryData = (systemUserId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/banking-partners-balance-by-country/${systemUserId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyPockets = (customerCompanyId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customer-company-pockets/${customerCompanyId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyComplianceUsers = (customerCompanyId: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customer-company-compliance-users/${customerCompanyId}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCurrentPocketsDataByCountry = (customerCompanyId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/pockets-data-by-country/${customerCompanyId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getSystemUsersByCountry = (customerCompanyId: string, selectedCountry: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/system-users-by-country/${customerCompanyId}/${selectedCountry}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomersDataWithSameBankAccount = (customerCompanyId: string, customerId: string, bankAccountNumber: string, accessToken: string) => {
  return AppDataHttp.get(`/app-queries/customers-with-same-bank-account/${customerCompanyId}/${customerId}/${bankAccountNumber}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getBatchByName = (batchName: string, customerCompanyId: string, formData: FormData, accessToken: string) => {
  return AppDataHttp.post(`/app-queries/batch-by-name/${customerCompanyId}/${batchName}`, formData, {
    headers: {
      'content-type': 'multipart/form-data',
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getCustomerCompanyMoneyMovements = (customerCompanyId: string, currency: string, accessToken: string) => {
  return AppDataHttp.get(`app-queries/customer-company-money-movements/${customerCompanyId}/${currency}`, {
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    }
  })
}

export const getBalanceBySelectedBankingPartner = (selectedCountry: string, selectedBankingPartner: string, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/get-balance-by-selected-banking-partner',
    {
      selectedCountry,
      selectedBankingPartner
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const sendPaymentTest = (amountToSend: string | number, customerData: Partial<Customer> | null, selectedCountry: string, selectedCurrency: string, selectedBankingPartner: string, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/send-payment-test',
    {
      amountToSend,
      customerData,
      selectedCountry,
      selectedCurrency,
      selectedBankingPartner
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const searchCustomersByRegex = (payload: object, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/customers-to-search',
    {
      ...payload
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const searchBatchesByRegex = (payload: object, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/batches-to-search',
    {
      ...payload
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}

export const searchPaymentsByRegex = (payload: object, accessToken: string) => {
  return AppDataHttp.post(
    '/app-queries/payments-to-search',
    {
      ...payload
    },
    {
      headers: {
        ...headers,
        Authorization: `Bearer ${accessToken}`
      }
    }
  )
}
